import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Tv, List, FolderOpen, Bell, CreditCard, Settings, LogOut, X, Users, Smile, Dna, Trophy, MonitorPlay, Cast, MoreHorizontal, BarChart2, Compass, ShieldCheck
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';

const QUDE_EXTENSION_URL = 'https://chromewebstore.google.com/';

// Grouped instead of one flat 14-item list -- the old version gave "Hub"
// and "Folders" the exact same visual weight, no signal for what matters
// (the watch-something surfaces) vs. what's utility (library upkeep,
// account). Remote stays filtered to Android-only same as before, just
// inside its group now instead of at the top level.
const navSections = [
  {
    label: 'Watch',
    items: [
      { to: '/hub',      icon: MonitorPlay, label: 'Hub' },
      { to: '/discover', icon: Compass,     label: 'Discover' },
      { to: '/mood',     icon: Smile,       label: 'MoodQ' },
      { to: '/remote',   icon: Cast,        label: 'Remote', androidOnly: true },
    ],
  },
  {
    label: 'Library',
    items: [
      { to: '/watchlist',      icon: List,        label: 'Watchlist' },
      { to: '/services',       icon: Tv,          label: 'Services' },
      { to: '/folders',        icon: FolderOpen,  label: 'Folders' },
      { to: '/reminders',      icon: Bell,        label: 'Reminders' },
      { to: '/spoiler-shield', icon: ShieldCheck, label: 'Spoiler Shield' },
      { to: '/history',        icon: BarChart2,   label: 'History' },
      { to: '/watch-dna',      icon: Dna,         label: 'Watch DNA' },
    ],
  },
  {
    label: 'More',
    items: [
      { to: '/qmate',    icon: Users,      label: 'QMate' },
      { to: '/sports',   icon: Trophy,     label: 'Sports' },
      { to: '/pricing',  icon: CreditCard, label: 'Pricing' },
      { to: '/settings', icon: Settings,   label: 'Settings' },
    ],
  },
];

function AppBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    let stars = [];
    let streaks = [];
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    for (let i = 0; i < 80; i++) {
      stars.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random(),
        speed: Math.random() * 0.01 + 0.005,
        phase: Math.random() * Math.PI * 2,
      });
    }
    for (let i = 0; i < 6; i++) {
      streaks.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        len: Math.random() * 80 + 40,
        alpha: 0,
        speed: Math.random() * 1.5 + 1,
        delay: Math.random() * 300,
        color: i % 2 === 0 ? '124,58,237' : '6,182,212',
      });
    }
    const rings = [1, 2, 3, 4].map((i) => ({
      r: 100 + i * 90,
      phase: Math.random() * Math.PI * 2,
      speed: 0.008 + i * 0.002,
      color: i % 2 === 0 ? '124,58,237' : '139,92,246',
    }));
    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      rings.forEach((ring) => {
        const alpha = 0.06 + 0.08 * Math.sin(t * ring.speed + ring.phase);
        ctx.beginPath();
        ctx.arc(cx, cy, ring.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${ring.color},${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      stars.forEach((star) => {
        const alpha = 0.2 + 0.6 * Math.abs(Math.sin(t * star.speed + star.phase));
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      streaks.forEach((streak) => {
        if (t < streak.delay) return;
        streak.alpha = Math.min(streak.alpha + 0.02, 0.6);
        streak.x += streak.speed;
        if (streak.x > canvas.width + streak.len) {
          streak.x = -streak.len;
          streak.y = Math.random() * canvas.height;
          streak.alpha = 0;
          streak.delay = t + Math.random() * 200;
        }
        const grad = ctx.createLinearGradient(streak.x - streak.len, 0, streak.x, 0);
        grad.addColorStop(0, `rgba(${streak.color},0)`);
        grad.addColorStop(0.5, `rgba(${streak.color},${streak.alpha})`);
        grad.addColorStop(1, `rgba(${streak.color},0)`);
        ctx.beginPath();
        ctx.moveTo(streak.x - streak.len, streak.y);
        ctx.lineTo(streak.x, streak.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      t++;
      animFrame = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100%', height: '100%',
        zIndex: 0, pointerEvents: 'none',
      }}
    />
  );
}

function A11yOnboardingCard({ onDismiss }) {
  const isAndroid = typeof window !== 'undefined' && !!window.QudeAndroid;

  const handleEnableNow = () => {
    if (window.QudeAndroid?.openAccessibilitySettings) {
      window.QudeAndroid.openAccessibilitySettings();
    }
    onDismiss();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '0 0 24px 0',
    }}>
      <div style={{
        background: '#0f0f0f',
        border: '1px solid rgba(124,58,237,0.3)',
        borderRadius: 24,
        padding: '28px 24px 24px',
        maxWidth: 400,
        width: 'calc(100% - 32px)',
        boxShadow: '0 0 60px rgba(124,58,237,0.15)',
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(74,158,255,0.2))',
          border: '1px solid rgba(124,58,237,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 18,
        }}>
          <MonitorPlay size={24} style={{ color: '#a78bfa' }} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff', margin: '0 0 10px', lineHeight: 1.3 }}>
          Always know where you left off.
        </h2>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', margin: '0 0 8px', lineHeight: 1.6 }}>
          Enable auto-tracking and Qude remembers your spot on every show, every service, automatically.
        </p>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', margin: '0 0 24px', lineHeight: 1.5 }}>
          Only reads your streaming apps — never passwords or personal data.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {['N', 'H', 'D+', 'MAX', 'P', 'TV+', 'PK', 'P+'].map((label, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, padding: '4px 8px',
              fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)',
            }}>{label}</div>
          ))}
        </div>
        {isAndroid ? (
          <>
            <button onClick={handleEnableNow} style={{
              width: '100%', padding: '14px',
              background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)',
              border: 'none', borderRadius: 14,
              fontSize: 15, fontWeight: 700, color: '#fff',
              cursor: 'pointer', marginBottom: 10,
            }}>Enable Now</button>
            <button onClick={onDismiss} style={{
              width: '100%', padding: '12px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 14, fontSize: 14,
              color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
            }}>Maybe Later</button>
          </>
        ) : (
          <button onClick={onDismiss} style={{
            width: '100%', padding: '14px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14, fontSize: 14,
            color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
          }}>Got it</button>
        )}
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgoShell(iso) {
  if (!iso) return '';
  const delta = (Date.now() - new Date(iso)) / 1000;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

export default function AppShell() {
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  // window.QudeAndroid (the native bridge) isn't reliably present on the
  // very first synchronous render inside the WebView -- same race Settings.js
  // already works around with a "detect with retries" effect. A one-shot
  // synchronous check here got stuck reading false forever on real devices,
  // hiding Remote in the native app too, not just the website.
  const [isAndroid, setIsAndroid] = useState(false);
  useEffect(() => {
    const check = () => { if (window.QudeAndroid) setIsAndroid(true); };
    check();
    const t1 = setTimeout(check, 500);
    const t2 = setTimeout(check, 1000);
    const t3 = setTimeout(check, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showA11yPrompt, setShowA11yPrompt] = useState(false);
  const [showExtBanner, setShowExtBanner] = useState(false);

  // showExtBanner intentionally never flips true as of 2026-09-25: its
  // "Install" button pointed at QUDE_EXTENSION_URL, which was just the bare
  // chromewebstore.google.com homepage -- the Qude extension has never
  // actually been submitted to the Chrome Web Store (no listing, no store
  // id anywhere in the repo; extension/ is sideload-only, its own history-
  // reader content scripts are still unverified against live logged-in
  // pages per that feature's own session notes). The banner was promising
  // a one-click install that 404s. Restore the effect below (was: detect
  // desktop + not dismissed + not installed via localStorage, after a
  // 200ms delay) once there's a real store listing, and point
  // QUDE_EXTENSION_URL at that listing's actual URL, not the store's bare
  // homepage.

  useEffect(() => {
    window.__showQudeA11yPrompt = () => setShowA11yPrompt(true);
    window.__onQudeA11yStatus = (enabled) => { if (enabled) setShowA11yPrompt(false); };
    if (window.__qudeA11yEnabled) setShowA11yPrompt(false);
    return () => {
      delete window.__showQudeA11yPrompt;
      delete window.__onQudeA11yStatus;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const checkUnread = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnreadCount(res.data.count || 0);
      } catch {}
    };
    checkUnread();
    const iv = setInterval(checkUnread, 60000);
    return () => clearInterval(iv);
  }, [user, getToken]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
    } catch {}
    setNotifLoading(false);
  };

  const openNotifications = () => {
    setNotifOpen(true);
    fetchNotifications();
    setUnreadCount(0);
  };

  const markAllRead = async () => {
    try {
      const token = await getToken();
      await axios.post(`${API}/notifications/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(0);
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user?.firstName?.[0] || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || '?';

  return (
    <div className="min-h-screen flex" style={{ background: '#050505' }} data-testid="app-shell">
      <AppBackground />

      {showA11yPrompt && (
        <A11yOnboardingCard onDismiss={() => setShowA11yPrompt(false)} />
      )}

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#0A0A0A]/90 backdrop-blur-xl border-r border-white/5 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="p-5 flex items-center justify-between border-b border-white/5">
          <img src="/logo.png" alt="Qude" className="w-10 h-10 rounded-lg object-contain" />
          <button
            onClick={openNotifications}
            className="relative p-2 text-neutral-500 hover:text-white transition-colors rounded-xl hover:bg-white/5"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-violet-500 text-white text-[9px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {navSections.map((section) => {
            const items = section.items.filter(item => !item.androidOnly || isAndroid);
            if (items.length === 0) return null;
            return (
              <div key={section.label} className="space-y-1">
                <p className="px-4 mb-1 text-[10px] font-black tracking-widest text-neutral-600 uppercase">
                  {section.label}
                </p>
                {items.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-violet-500/15 text-violet-400 border border-violet-500/20'
                          : 'text-neutral-400 hover:text-white hover:bg-white/5'
                      }`
                    }
                    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                    {label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5" style={{ paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' }}>
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.imageUrl} />
              <AvatarFallback className="bg-violet-600/20 text-violet-400 text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.fullName}</p>
              <p className="text-xs text-neutral-500 truncate">{user?.emailAddresses?.[0]?.emailAddress}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0" style={{ position: 'relative', zIndex: 1 }}>
        {/* Extension banner */}
        {showExtBanner && (
          <div className="flex items-center justify-between px-6 py-2.5 bg-[#0f0f0f] border-b border-violet-500/20 flex-shrink-0">
            <p className="text-sm text-neutral-300">Get the Qude Chrome Extension to auto-track what you watch</p>
            <div className="flex items-center gap-3 flex-shrink-0">
              <a
                href={QUDE_EXTENSION_URL}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold px-4 py-1.5 rounded-full text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}
              >
                Install
              </a>
              <button
                onClick={() => { localStorage.setItem('qude_ext_dismissed', 'true'); setShowExtBanner(false); }}
                className="p-1 text-neutral-500 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Page content — no overflow-hidden, no clipping */}
        <main className="flex-1 pb-24 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-3xl mx-auto px-4 py-4 md:py-8">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom tab bar */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/5 flex"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {[
            { to: '/hub',       icon: MonitorPlay, label: 'Hub' },
            { to: '/watchlist', icon: List,        label: 'Watchlist' },
            ...(isAndroid ? [{ to: '/remote', icon: Cast, label: 'Remote' }] : []),
            { to: '/sports',    icon: Trophy,      label: 'Sports' },
          ].map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-3 text-[10px] font-semibold transition-all ${
                  isActive ? 'text-violet-400' : 'text-neutral-500 hover:text-neutral-300'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex-1 flex flex-col items-center gap-0.5 py-3 text-[10px] font-semibold text-neutral-500 hover:text-neutral-300"
          >
            <MoreHorizontal className="w-5 h-5" />
            More
          </button>
        </nav>

        {/* Notification drawer */}
        {notifOpen && (
          <div className="fixed inset-0 z-50 flex" onClick={() => setNotifOpen(false)}>
            <div className="flex-1" />
            <div
              className="w-full max-w-sm bg-[#0f0f0f] border-l border-white/10 h-full overflow-y-auto flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-black" style={{ fontFamily: 'Outfit' }}>Notifications</h2>
                <div className="flex items-center gap-2">
                  {notifications.length > 0 && (
                    <button onClick={markAllRead} className="text-xs text-neutral-500 hover:text-violet-400 transition-colors px-2 py-1 rounded-lg">
                      Mark all read
                    </button>
                  )}
                  <button onClick={() => setNotifOpen(false)} className="p-1.5 text-neutral-500 hover:text-white transition-colors rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 space-y-2">
                {notifLoading ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-neutral-500">
                    <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <Bell className="w-10 h-10 text-neutral-700 mx-auto mb-3" />
                    <p className="text-neutral-500 text-sm">No notifications</p>
                    <p className="text-xs text-neutral-600 mt-1">Drop alerts, watch party invites, and QMate requests will appear here</p>
                  </div>
                ) : (
                  notifications.map(n => (
                    <button
                      key={n.id}
                      onClick={() => { setNotifOpen(false); navigate(n.action || '/hub'); }}
                      className="w-full text-left p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/30 transition-all space-y-1"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-white leading-tight">{n.title}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 font-semibold ${
                          n.type === 'qmate_invite' ? 'bg-violet-500/20 text-violet-400' :
                          n.type === 'drop_alert'   ? 'bg-[#4A9EFF]/20 text-[#4A9EFF]' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {n.type === 'qmate_invite' ? 'QMate' : n.type === 'drop_alert' ? 'Alert' : 'Party'}
                        </span>
                      </div>
                      {n.body && <p className="text-xs text-neutral-500">{n.body}</p>}
                      {n.created_at && <p className="text-[10px] text-neutral-700">{timeAgoShell(n.created_at)}</p>}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* More bottom sheet */}
        {moreOpen && (
          <div
            className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end bg-black/60"
            onClick={() => setMoreOpen(false)}
          >
            <div
              className="bg-[#0f0f0f] border-t border-white/10 rounded-t-3xl p-5"
              style={{ paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem))' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="w-10 h-1 bg-white/15 rounded-full mx-auto mb-5" />
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { to: '/discover',  icon: Compass,         label: 'Discover' },
                  ...(isAndroid ? [{ to: '/remote', icon: Cast, label: 'Remote' }] : []),
                  { to: '/services',  icon: Tv,              label: 'Services' },
                  { to: '/folders',   icon: FolderOpen,      label: 'Folders' },
                  { to: '/history',   icon: BarChart2,       label: 'History' },
                  { to: '/reminders', icon: Bell,            label: 'Reminders' },
                  { to: '/qmate',     icon: Users,           label: 'QMate' },
                  { to: '/mood',      icon: Smile,           label: 'MoodQ' },
                  { to: '/watch-dna', icon: Dna,             label: 'Watch DNA' },
                  { to: '/pricing',   icon: CreditCard,      label: 'Pricing' },
                  { to: '/settings',  icon: Settings,        label: 'Settings' },
                ].map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-1.5 p-3 rounded-2xl border text-center text-[11px] font-semibold transition-all ${
                        isActive
                          ? 'bg-violet-500/15 border-violet-500/20 text-violet-400'
                          : 'bg-[#1a1a1a] border-white/5 text-neutral-400 hover:text-white'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </NavLink>
                ))}
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/5 text-red-400 text-sm font-semibold hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}